import React, { useEffect, useState } from 'react';
import { KeyRound, X } from 'lucide-react';

import { ovhApi } from '../lib/ovhApi';

const MIN_PASSWORD_LENGTH = 12;

function PasswordFields({
  password,
  confirmation,
  onPasswordChange,
  onConfirmationChange,
}) {
  return (
    <>
      <label>
        Nouveau mot de passe
        <input
          type="password"
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
      </label>

      <label>
        Confirmer le nouveau mot de passe
        <input
          type="password"
          value={confirmation}
          onChange={(event) => onConfirmationChange(event.target.value)}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
      </label>
    </>
  );
}

export function RecoveryPasswordPage({
  type,
  token,
  onAuthenticated,
  onCancel,
}) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    ovhApi
      .tokenStatus(type, token)
      .then((result) => {
        if (active) setValid(result.valid === true);
      })
      .catch(() => {
        if (active) setValid(false);
      })
      .finally(() => {
        if (active) setChecking(false);
      });

    return () => {
      active = false;
    };
  }, [type, token]);

  async function submit(event) {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setMessage(
        `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`
      );
      return;
    }

    if (password !== confirmation) {
      setMessage('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      const result = await ovhApi.setPassword(
        type,
        token,
        password,
        confirmation
      );

      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );
      setMessage(
        'Votre nouveau mot de passe est enregistré. Ouverture du tableau de bord…'
      );
      window.setTimeout(() => onAuthenticated(result.user), 700);
    } catch (error) {
      setMessage(error.message);
      setSubmitting(false);
    }
  }

  if (checking) {
    return (
      <div className="application-loading">
        Vérification du lien sécurisé…
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="login-shell">
        <section className="panel login">
          <div className="login-brand">
            <div className="login-logo">
              <KeyRound size={24} />
            </div>
            <div>
              <h1>Lien invalide ou expiré</h1>
              <p>Geoffroy et Fils</p>
            </div>
          </div>
          <div className="login-introduction">
            Demandez un nouveau lien depuis la page de connexion.
          </div>
          <button type="button" className="primary" onClick={onCancel}>
            Revenir à la connexion
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <form className="panel login" onSubmit={submit}>
        <div className="login-brand">
          <div className="login-logo">
            <KeyRound size={24} />
          </div>

          <div>
            <h1>Créer un nouveau mot de passe</h1>
            <p>Geoffroy et Fils</p>
          </div>
        </div>

        <div className="login-introduction">
          Choisissez un mot de passe d’au moins {MIN_PASSWORD_LENGTH} caractères.
        </div>

        <PasswordFields
          password={password}
          confirmation={confirmation}
          onPasswordChange={setPassword}
          onConfirmationChange={setConfirmation}
        />

        <button type="submit" className="primary" disabled={submitting}>
          {submitting ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
        </button>

        {message && <div className="message">{message}</div>}
      </form>
    </div>
  );
}

export function ChangePasswordModal({ open, onClose, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function submit(event) {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setMessage(
        `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`
      );
      return;
    }

    if (password !== confirmation) {
      setMessage('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      await ovhApi.changePassword(
        currentPassword,
        password,
        confirmation
      );
      setCurrentPassword('');
      setPassword('');
      setConfirmation('');
      onSuccess('Votre mot de passe a été modifié.');
      onClose();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 id="change-password-title">Modifier mon mot de passe</h2>
            <p>La modification prend effet immédiatement.</p>
          </div>

          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={19} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="modal-content">
            <label>
              Mot de passe actuel
              <input
                type="password"
                value={currentPassword}
                onChange={(event) =>
                  setCurrentPassword(event.target.value)
                }
                autoComplete="current-password"
                required
              />
            </label>

            <PasswordFields
              password={password}
              confirmation={confirmation}
              onPasswordChange={setPassword}
              onConfirmationChange={setConfirmation}
            />

            {message && <div className="message">{message}</div>}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Annuler
            </button>

            <button
              type="submit"
              className="primary modal-primary"
              disabled={submitting}
            >
              {submitting ? 'Enregistrement…' : 'Modifier le mot de passe'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
